<template>
  <div
    ref="wrapperRef"
    class="base-widget-wrapper"
    @mouseenter="onHover"
  >
    <div :class="['base-widget', customClass]">
      <div class="widget-header">
        <div class="widget-title-row">
          <h3 class="widget-title">
            {{ title }}
          </h3>
          <slot name="header-icon" />
        </div>
        <div
          class="widget-header-actions"
          :class="{ 'has-header-chip': !!$slots['header-chip'] }"
        >
          <slot name="header-chip" />
          <WidgetInfoIcon
            v-if="showInfoIcon && !editMode"
            class="header-info-icon"
            :tooltip="infoTooltip"
            :link="infoTooltipLink"
          />
          <button
            v-if="editMode && widgetId"
            class="delete-btn"
            aria-label="Remove widget"
            @click.prevent="handleDeleteWidget"
            @mousedown.prevent
            @mouseenter="showTooltip($event, 'Remove widget')"
            @mouseleave="hideTooltip"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"
                fill="currentColor"
              />
            </svg>
          </button>
          <div
            v-if="editMode && widgetId"
            class="date-range-wrapper"
            @mouseenter="showDateRangeTooltip"
            @mouseleave="hideTooltip"
          >
            <button
              ref="dateRangeBtnRef"
              class="date-range-btn"
              :class="{ disabled: disableDateRange }"
              :disabled="disableDateRange"
              aria-label="Change date range"
              @click.prevent="toggleDateDropdown"
              @mousedown.prevent
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <Teleport to="body">
              <div
                v-if="tooltipVisible"
                class="widget-icon-tooltip"
                :style="tooltipStyle"
              >
                {{ tooltipText }}
              </div>
              <div
                v-if="dateDropdownOpen"
                class="date-range-dropdown"
                :style="dropdownStyle"
              >
                <button
                  v-for="option in dateRangeOptions"
                  :key="option.value"
                  class="date-range-option"
                  :class="{ active: option.value === dateRange }"
                  @click.prevent="selectDateRange(option.value)"
                  @mousedown.prevent
                >
                  {{ option.label }}
                </button>
              </div>
            </Teleport>
          </div>
          <div
            v-if="editMode && widgetId && overflowMenuItems.length > 0"
            class="overflow-menu-wrapper"
          >
            <button
              ref="overflowBtnRef"
              class="overflow-btn"
              aria-label="More options"
              @click.prevent="toggleOverflowMenu"
              @mousedown.prevent
              @mouseenter="showTooltip($event, 'More options')"
              @mouseleave="hideTooltip"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="5"
                  r="2"
                  fill="currentColor"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="2"
                  fill="currentColor"
                />
                <circle
                  cx="12"
                  cy="19"
                  r="2"
                  fill="currentColor"
                />
              </svg>
            </button>
            <Teleport to="body">
              <div
                v-if="overflowMenuOpen"
                ref="overflowDropdownRef"
                class="overflow-menu-dropdown"
                :style="overflowMenuStyle"
              >
                <button
                  v-for="item in overflowMenuItems.filter(i => !i.active)"
                  :key="item.value"
                  class="overflow-menu-option"
                  @click.prevent="selectOverflowItem(item)"
                  @mousedown.prevent
                >
                  {{ item.label }}
                </button>
              </div>
            </Teleport>
          </div>
        </div>
      </div>

      <div class="widget-content">
        <slot />
      </div>
      <slot name="footer" />
      <AutomationsFooterBar
        v-if="showAutomationsFooter"
        :widget-type="widgetType"
        :title="title"
        :edit-mode="editMode"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, inject } from 'vue'
import WidgetInfoIcon from './WidgetInfoIcon.vue'
import AutomationsFooterBar from './AutomationsFooterBar.vue'
import { WIDGET_TO_BEST_PRACTICE_TYPES, AUTOMATIONS_SECTION_WIDGET_TYPES } from '../utils/widgetBestPractices.js'
import { useAutomationsFooter } from '../composables/useAutomationsFooter.js'
import { getDateRangeOptions } from '../utils/dateRanges.js'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  showInfoIcon: {
    type: Boolean,
    default: false
  },
  /** Tooltip text for info icon. When provided, shows tooltip on hover with optional link. */
  infoTooltip: {
    type: String,
    default: ''
  },
  /** Optional link { url, text } for info tooltip (e.g. { url: '...', text: 'Learn more' }) */
  infoTooltipLink: {
    type: Object,
    default: null
  },
  customClass: {
    type: String,
    default: ''
  },
  editMode: {
    type: Boolean,
    default: false
  },
  showSettings: {
    type: Boolean,
    default: true
  },
  widgetId: {
    type: String,
    default: null
  },
  /** Widget type for panel best practices (e.g. 'upcoming-tours') */
  widgetType: {
    type: String,
    default: null
  },
  rowSpan: {
    type: Number,
    default: null
  },
  colSpan: {
    type: Number,
    default: null
  },
  /** Current date range value */
  dateRange: {
    type: String,
    default: null
  },
  /** Only show past date range options */
  pastOnly: {
    type: Boolean,
    default: false
  },
  /** Only show future date range options */
  futureOnly: {
    type: Boolean,
    default: false
  },
  /** Disable the date range picker (show greyed out icon) */
  disableDateRange: {
    type: Boolean,
    default: false
  },
  /** Overflow menu items: [{ label, value, active }] */
  overflowMenuItems: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['open-settings', 'remove-widget', 'update:date-range', 'overflow-select'])

const automationsFooterEnabled = useAutomationsFooter()
const showAutomationsFooter = computed(() =>
  automationsFooterEnabled.value &&
  !props.editMode &&
  (WIDGET_TO_BEST_PRACTICE_TYPES[props.widgetType]?.length > 0) &&
  !AUTOMATIONS_SECTION_WIDGET_TYPES.includes(props.widgetType)
)

// Tooltip
const tooltipVisible = ref(false)
const tooltipText = ref('')
const tooltipStyle = ref({})

const showTooltip = (e, text) => {
  const rect = e.currentTarget.getBoundingClientRect()
  tooltipText.value = text
  tooltipStyle.value = {
    position: 'fixed',
    left: `${rect.left + rect.width / 2}px`,
    top: `${rect.top - 8}px`,
    transform: 'translate(-50%, -100%)',
    zIndex: 10001
  }
  tooltipVisible.value = true
}

const hideTooltip = () => {
  tooltipVisible.value = false
}

const showDateRangeTooltip = () => {
  if (!dateRangeBtnRef.value) return
  const rect = dateRangeBtnRef.value.getBoundingClientRect()
  tooltipText.value = props.disableDateRange
    ? 'Date range not available for this widget'
    : 'Change date range'
  tooltipStyle.value = {
    position: 'fixed',
    left: `${rect.left + rect.width / 2}px`,
    top: `${rect.top - 8}px`,
    transform: 'translate(-50%, -100%)',
    zIndex: 10001
  }
  tooltipVisible.value = true
}

// Date range dropdown
const dateDropdownOpen = ref(false)
const dateRangeBtnRef = ref(null)
const dropdownStyle = ref({})
const dateRangeOptions = computed(() => getDateRangeOptions(props.pastOnly, props.futureOnly))

const updateDropdownPosition = () => {
  if (!dateRangeBtnRef.value) return
  const rect = dateRangeBtnRef.value.getBoundingClientRect()
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: 10000
  }
}

const closeDateDropdown = () => {
  dateDropdownOpen.value = false
}

const toggleDateDropdown = () => {
  if (!dateDropdownOpen.value) {
    // Close any other open date dropdowns first
    window.dispatchEvent(new CustomEvent('close-all-date-dropdowns'))
    updateDropdownPosition()
  }
  dateDropdownOpen.value = !dateDropdownOpen.value
}

const selectDateRange = (value) => {
  dateDropdownOpen.value = false
  emit('update:date-range', value)
}

// Overflow menu
const overflowMenuOpen = ref(false)
const overflowBtnRef = ref(null)
const overflowDropdownRef = ref(null)
const overflowMenuStyle = ref({})

const updateOverflowMenuPosition = () => {
  if (!overflowBtnRef.value) return
  const rect = overflowBtnRef.value.getBoundingClientRect()
  overflowMenuStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: 10000
  }
}

const toggleOverflowMenu = () => {
  if (!overflowMenuOpen.value) {
    updateOverflowMenuPosition()
  }
  overflowMenuOpen.value = !overflowMenuOpen.value
}

const selectOverflowItem = (item) => {
  overflowMenuOpen.value = false
  emit('overflow-select', item.value)
}

const handleClickOutside = (e) => {
  if (dateDropdownOpen.value && !e.target.closest('.date-range-wrapper')) {
    dateDropdownOpen.value = false
  }
  if (overflowMenuOpen.value &&
    !(overflowBtnRef.value && overflowBtnRef.value.contains(e.target)) &&
    !(overflowDropdownRef.value && overflowDropdownRef.value.contains(e.target))) {
    overflowMenuOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  window.addEventListener('close-all-date-dropdowns', closeDateDropdown)
})

// PostHog tracking for automation widgets (viewed + hovered)
const trackPosthogEvent = inject('trackPosthogEvent', () => {})
const wrapperRef = ref(null)
const isAutomationWidget = computed(() =>
  AUTOMATIONS_SECTION_WIDGET_TYPES.includes(props.widgetType)
)

let hasTrackedView = false
let hasTrackedHover = false
let observer = null

onMounted(() => {
  if (!isAutomationWidget.value || !wrapperRef.value) return
  observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting && !hasTrackedView) {
      hasTrackedView = true
      trackPosthogEvent('Dashboard: Automation widget viewed', { widgetType: props.widgetType })
      observer.disconnect()
    }
  }, { threshold: 0.5 })
  observer.observe(wrapperRef.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
  document.removeEventListener('click', handleClickOutside, true)
  window.removeEventListener('close-all-date-dropdowns', closeDateDropdown)
})

const onHover = () => {
  if (!isAutomationWidget.value || hasTrackedHover) return
  hasTrackedHover = true
  trackPosthogEvent('Dashboard: Automation widget hovered', { widgetType: props.widgetType })
}

const handleDeleteWidget = () => {
  if (props.widgetId && confirm('Remove widget?')) {
    emit('remove-widget', props.widgetId)
  }
}
</script>

<style scoped>
.base-widget {
  background: white;
  border-radius: 12px;
  font-size: 12px;
  padding: 18px 20px 10px 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: none;
  box-shadow: none;
  container-type: size;
}


.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  min-height: 22px;
  position: relative;
}

.widget-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.widget-title {
  font-size: 15px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
  line-height: 1.15;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.widget-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.base-widget-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.base-widget-wrapper > .base-widget {
  flex: 1;
  min-height: 0;
}

/* Info icon: show when widget is hovered anywhere */
.base-widget-wrapper:hover :deep(.widget-info-icon-wrapper .info-icon),
.base-widget:hover :deep(.widget-info-icon-wrapper .info-icon) {
  opacity: 1;
}

/* Header chip (e.g. "Updated"): when a chip is present, collapse the info icon while
   idle so the chip sits flush at the top-right, then expand it on hover so the chip
   slides to the left of the (i). */
.widget-header-actions.has-header-chip :deep(.header-info-icon) {
  max-width: 0;
  margin-left: -8px; /* cancel the flex gap so the chip is flush right */
  overflow: hidden;
  transition: max-width 0.2s ease, margin-left 0.2s ease;
}

.base-widget-wrapper:hover .widget-header-actions.has-header-chip :deep(.header-info-icon),
.base-widget:hover .widget-header-actions.has-header-chip :deep(.header-info-icon) {
  max-width: 24px;
  margin-left: 0;
}

/* Touch devices have no hover — keep the info icon reachable next to the chip */
@media (hover: none) {
  .widget-header-actions.has-header-chip :deep(.header-info-icon) {
    max-width: 24px;
    margin-left: 0;
  }
}

/* "View all" link: show only on widget hover (desktop), always visible on touch devices */
.base-widget-wrapper :deep(.focused-view-all-link) {
  opacity: 0;
  transition: opacity 0.2s ease, color 0.2s ease, max-width 0.25s ease, margin 0.25s ease;
}

.base-widget-wrapper:hover :deep(.focused-view-all-link) {
  opacity: 1;
}

/* Touch devices have no hover — always show "View all" links */
@media (hover: none) {
  .base-widget-wrapper :deep(.focused-view-all-link) {
    opacity: 1;
  }
}

.base-widget-wrapper:hover :deep(.focused-footer .focused-view-all-link) {
  max-width: 100px;
  margin-left: 10px;
}

@media (hover: none) {
  .base-widget-wrapper :deep(.focused-footer .focused-view-all-link) {
    max-width: 100px;
    margin-left: 10px;
  }
}

.delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  padding: 0;
  margin: -3px 0;
  opacity: 0;
}

.base-widget-wrapper:hover .delete-btn {
  opacity: 1;
}

.delete-btn:hover {
  background: #f3f4f6;
  color: rgba(0, 0, 0, 0.6);
}

.delete-btn:active {
  background: #e5e7eb;
  color: rgba(0, 0, 0, 0.6);
}

.date-range-wrapper {
  position: relative;
  display: flex;
}

.date-range-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  padding: 0;
  margin: -3px 0;
  opacity: 0;
}

.base-widget-wrapper:hover .date-range-btn {
  opacity: 1;
}

.date-range-btn:hover:not(.disabled) {
  background: #f3f4f6;
  color: rgba(0, 0, 0, 0.6);
}

.date-range-btn:active:not(.disabled) {
  background: #e5e7eb;
}

.date-range-btn.disabled {
  cursor: default;
}

.base-widget-wrapper:hover .date-range-btn.disabled {
  opacity: 0.38;
}

:global(.widget-icon-tooltip) {
  background: #333;
  color: white;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  padding: 5px 9px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
}

.overflow-menu-wrapper {
  position: relative;
  display: flex;
}

.overflow-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  padding: 0;
  margin: -3px 0;
  opacity: 0;
}

.base-widget-wrapper:hover .overflow-btn {
  opacity: 1;
}

.overflow-btn:hover {
  background: #f3f4f6;
  color: rgba(0, 0, 0, 0.6);
}

.overflow-btn:active {
  background: #e5e7eb;
}

:global(.overflow-menu-dropdown) {
  background: white;
  border-radius: 4px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.14), 0 3px 6px rgba(0, 0, 0, 0.1);
  min-width: 160px;
  padding: 8px 0;
  overflow: hidden;
}

:global(.overflow-menu-option) {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

:global(.overflow-menu-option:hover) {
  background: #f3f4f6;
}

:global(.overflow-menu-option.active) {
  color: #2383E2;
  font-weight: 500;
}

:global(.date-range-dropdown) {
  background: white;
  border-radius: 4px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.14), 0 3px 6px rgba(0, 0, 0, 0.1);
  min-width: 160px;
  padding: 8px 0;
  overflow: hidden;
}

:global(.date-range-option) {
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

:global(.date-range-option:hover) {
  background: #f3f4f6;
}

:global(.date-range-option.active) {
  color: #2383E2;
  font-weight: 500;
}

.widget-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 0;
  overflow: hidden;
}
</style>

<style>
/* Allow automations footer to overflow widget bounds on hover (unscoped to cross component boundary) */
.base-widget-wrapper:has([data-automations-footer]:hover) {
  overflow: visible !important;
}

.base-widget-wrapper:has([data-automations-footer]:hover) > .base-widget {
  overflow: visible !important;
  container-type: normal;
}
</style>
